import { SelectChangeEvent } from '@mui/material/Select';
import { AxiosError } from 'axios';
import { FC, Fragment, useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { GroupListMemberQueryDto } from '@bom-nextgen-keycloak/models';
import {
  CONFIG,
  selectPermissionGroupDetail,
  useAlertMessage,
  useResource,
} from '@bom-nextgen-keycloak/web/core';
import { UserTable2 } from '@bom-nextgen-keycloak/web/pages/feature-user';
import {
  ErrorMessage,
  FeaturePage,
  fetchgroupListMembers,
  QUERY_KEY,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  RefreshButton,
  ResetButton,
  Searchbox,
  SearchboxEmit,
  SearchboxOptions,
  ProtectedRouter,
  Toolbar,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  checkProcessing,
  generateHash,
  useDebounce,
} from '@bom-nextgen-keycloak/web/shared/util';
import { PARENT_PATH } from '../../shared';

const GroupMember2: FC = () => {
  const { id = '' } = useParams();
  const resource = useResource();
  const { groupId: resourceGroupId } = resource;
  const defaultParams = {
    first: CONFIG.pagination.offset,
    id,
    max: CONFIG.pagination.step,
  };
  const defaultData = {
    items: [],
    totalCount: 0,
    totalRecord: 0,
  };
  const { setAlertMessage } = useAlertMessage();
  const { isEditMemberTab: canEdit } = useSelector(selectPermissionGroupDetail);
  const [params, setParams] = useState<GroupListMemberQueryDto>({
    ...defaultParams,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentOffset, setCurrentOffset] = useState(CONFIG.pagination.offset);
  const [previousOffset, setPreviousOffset] = useState(
    CONFIG.pagination.offset
  );
  const [pageStep, setPageStep] = useState(CONFIG.pagination.step);
  const [queryHash, setQueryHash] = useState(generateHash());
  const [isSearching, setIsSearching] = useState(false);
  const [wasSearch, setWasSearch] = useState(false);
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    CONFIG.ui.debounceDelay
  );

  const isSearch = searchQuery !== '';
  const queryKeys = [
    QUERY_KEY.GROUP_MEMBERS,
    resourceGroupId,
    id,
    queryHash,
    debouncedSearchQuery,
  ];

  const {
    data = defaultData,
    isFetched,
    isFetching,
    isLoading,
  } = useQuery(
    queryKeys,
    () =>
      fetchgroupListMembers(resourceGroupId, {
        ...params,
        search: searchQuery,
      }),
    {
      onError: (error: AxiosError<ErrorMessage>) => {
        const message =
          error.response?.data.message || 'Cannot get a list of group members';

        setAlertMessage({
          message,
          statusCode: error.response?.status,
          typeStatusMessage: 'error',
        });
      },
      onSuccess: () => {
        if (isSearch && isSearching) {
          setIsSearching(false);
        }

        if (wasSearch && !isSearch) {
          setWasSearch(false);
        }

        if (!isSearch) {
          setPreviousOffset(params.first);
        }
      },
      staleTime: CONFIG.query.staleTime,
    }
  );

  const isProcessing = checkProcessing({ isFetched, isFetching, isLoading });

  const handleRefetch = () => {
    setQueryHash(generateHash());
  };

  const handleRefresh = () => {
    const offset = 0;

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));
    handleRefetch();
  };

  const handleSearch = ({ event, isEnter = false }: SearchboxOptions) => {
    const { value } = event;
    const keyword = value.trim();
    const hasKeyword = keyword !== '';
    const hasChanged = keyword !== '' && keyword !== searchQuery;
    const offset = hasKeyword ? 0 : previousOffset;

    setCurrentOffset(offset);
    setIsSearching(hasChanged);
    setParams((previous) => ({
      ...previous,
      first: offset,
      isAll: hasKeyword,
    }));

    if (isEnter && hasKeyword) {
      handleRefetch();
    } else {
      setSearchQuery((previous) => {
        setWasSearch(previous !== '' && !hasKeyword);

        return value;
      });
    }
  };

  const handleSearchChange = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: false });
  };

  const handleSearchEnter = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: true });
  };

  const handleSearchReset = () => {
    const value = '';
    const offset = previousOffset;

    setCurrentOffset(offset);
    setWasSearch(true);
    setParams((previous) => ({
      ...previous,
      first: offset,
      isAll: false,
      search: value,
    }));
    setSearchQuery(value);
  };

  const handleSearchClear = () => {
    const value = '';
    const hasKeyword = isSearch;

    if (hasKeyword) {
      handleSearchReset();
    } else {
      // NOTE: Clear whitespaces without making an API request
      setSearchQuery(value);
    }
  };

  const handlePageChange = (offset: number) => {
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));

    setCurrentOffset(offset);
    setQueryHash(generateHash());
  };

  const handleStepChange = (event: SelectChangeEvent<unknown>) => {
    const step = event.target.value as number;
    const offset = 0;

    setCurrentOffset(offset);
    setPageStep(step);

    setParams((previous) => ({
      ...previous,
      first: offset,
      max: step,
    }));

    setQueryHash(generateHash());
  };

  return (
    <Fragment>
      <Toolbar>
        <Toolbar.Control>
          <Toolbar.Search>
            <Searchbox
              autoFocus
              name="name"
              onChange={handleSearchChange}
              onClear={handleSearchClear}
              onEnter={handleSearchEnter}
              placeholder="Search by username, email or name"
              value={searchQuery}
            />
            <ResetButton disabled={!isSearch} onClick={handleSearchReset}>
              Reset Search
            </ResetButton>
          </Toolbar.Search>
          <Toolbar.Action>
            <RefreshButton onClick={handleRefresh}>Refresh</RefreshButton>
          </Toolbar.Action>
        </Toolbar.Control>
      </Toolbar>
      <UserTable2
        canEdit={canEdit}
        data={data.items}
        isFetched={isFetched}
        isFetching={isFetching}
        isLoading={isLoading}
        isProcessing={isProcessing}
        isSearch={isSearch}
        isSearching={isSearching}
        paginationOffset={currentOffset}
        paginationStep={pageStep}
        onPageChange={handlePageChange}
        onStepChange={handleStepChange}
        totalCount={data.totalCount}
        totalRecord={data.totalRecord}
        wasSearch={wasSearch}
      />
    </Fragment>
  );
};

const WrappedGroupMemberPage2: FC = () => {
  const { canViewMemberTab } = useSelector(selectPermissionGroupDetail);

  return (
    <ProtectedRouter
      canView={canViewMemberTab}
      feature={FeaturePage.GROUP}
      isEditPage
      navigatePath={PARENT_PATH}
    >
      <GroupMember2 />
    </ProtectedRouter>
  );
};

export { WrappedGroupMemberPage2 };
